import { useMemo } from "react";
import type { BookmarkValue } from "@/storage/db";
import { BookmarkCard } from "./bookmark-card";

export type BookmarkFilter = "all" | "in-progress" | "completed";

interface BookmarkListProps {
  bookmarks: readonly BookmarkValue[];
  filter?: BookmarkFilter;
  limit?: number;
  emptyMessage?: string;
}

const matches = (filter: BookmarkFilter, bookmark: BookmarkValue): boolean => {
  if (filter === "in-progress") {
    return !bookmark.completed && bookmark.currentPage > 0;
  }
  if (filter === "completed") return bookmark.completed === true;
  return true;
};

export function selectBookmarksNewestFirst(
  bookmarks: readonly BookmarkValue[],
  filter: BookmarkFilter,
  limit?: number,
): BookmarkValue[] {
  const sorted = bookmarks
    .filter((bookmark) => matches(filter, bookmark))
    .toSorted((a, b) => b.lastupdate - a.lastupdate);
  return limit === undefined ? sorted : sorted.slice(0, limit);
}

export function BookmarkList({
  bookmarks: allBookmarks,
  filter = "all",
  limit,
  emptyMessage = "No bookmarks yet. Start reading to save your progress!",
}: BookmarkListProps) {
  const bookmarks = useMemo(
    () => selectBookmarksNewestFirst(allBookmarks, filter, limit),
    [allBookmarks, filter, limit],
  );

  if (bookmarks.length === 0) {
    return (
      <div className="text-muted-foreground rounded-lg border border-dashed p-8 text-center">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {bookmarks.map((bookmark) => (
        <BookmarkCard
          key={bookmark.readingId}
          bookmark={bookmark}
          showProgress={true}
        />
      ))}
    </div>
  );
}
